import { P, PasswordStrength, theme } from '@salecycle/ui-component-library';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  checkHasNumbers,
  checkHasUppercase,
  checkHasLowercase,
  checkHasSpecial,
  checkHasValidLength,
} from './validPasswordChecks';

const SliderContainer = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  margin: 0.5rem 0 3rem;
  padding: 1.5rem 2rem;
  background: ${({ theme }) => theme.vars.newColor.white};
`;

interface IProps {
  handleFormChange?: (strength: number, password: string) => void;
}
export const NewPasswordStrengthStory: React.FC<IProps> = props => {
  const { t } = useTranslation('login');
  const [password, setPassword] = React.useState('');

  let matches: string[];
  let strength: number;
  let width: number;
  let hasValidLength = false;
  let hasNumbers = false;
  let hasUppercase = false;
  let hasLowercase = false;
  let hasSpecial = false;
  const numberOfPasswordRules = 5;

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    // On change, set the password value then get the Slider content
    SliderContent();
    const upToDataStrength = strengthMatches(value).length;
    props.handleFormChange(upToDataStrength, value);
  };

  // Get slider content
  const SliderContent = () => {
    // Get back which criteria is matched
    matches = strengthMatches(password);
    strength = matches.length;
    // Get back the colour of the bar
    // Work out the width of the criteria strength bar
    width = (strength / numberOfPasswordRules) * 100;
    return checkCriteria();
  };

  const checkCriteria = () => {
    hasValidLength = false;
    hasNumbers = false;
    hasUppercase = false;
    hasLowercase = false;
    hasSpecial = false;

    if (matches.length > 0) {
      matches.forEach(match => {
        switch (match) {
          case 'has-valid-length':
            hasValidLength = true;
            break;
          case 'has-numbers':
            hasNumbers = true;
            break;
          case 'has-uppercase':
            hasUppercase = true;
            break;
          case 'has-lowercase':
            hasLowercase = true;
            break;
          case 'has-special':
            hasSpecial = true;
            break;
        }
      });
    }
    return (
      <>
        <P bold={true} fontSize="1.6rem">
          {t('resetPassword.passwordStrength.requirements.title')}
        </P>

        <P bold={true} color={hasValidLength ? theme.vars.newColor.green1 : theme.vars.newColor.red2}>
          {t('resetPassword.passwordStrength.requirements.characterLimit')}
        </P>

        <P bold={true} color={hasNumbers ? theme.vars.newColor.green1 : theme.vars.newColor.red2}>
          {t('resetPassword.passwordStrength.requirements.number')}
        </P>

        <P bold={true} color={hasSpecial ? theme.vars.newColor.green1 : theme.vars.newColor.red2}>
          {t('resetPassword.passwordStrength.requirements.specialCharacter')}
        </P>

        <P bold={true} color={hasUppercase ? theme.vars.newColor.green1 : theme.vars.newColor.red2}>
          {t('resetPassword.passwordStrength.requirements.uppercaseCharacter')}
        </P>

        <P bold={true} color={hasLowercase ? theme.vars.newColor.green1 : theme.vars.newColor.red2}>
          {t('resetPassword.passwordStrength.requirements.lowercaseCharacter')}
        </P>
      </>
    );
  };

  // Check requirements, if they are met, push to an array
  const strengthMatches = (value: string) => {
    const matched: string[] = [];

    if (checkHasValidLength(value)) {
      matched.push('has-valid-length');
    }
    if (checkHasNumbers(value)) {
      matched.push('has-numbers');
    }
    if (checkHasUppercase(value)) {
      matched.push('has-uppercase');
    }
    if (checkHasLowercase(value)) {
      matched.push('has-lowercase');
    }
    if (checkHasSpecial(value)) {
      matched.push('has-special');
    }
    return matched;
  };

  const sliderContent = SliderContent();
  return (
    <div>
      <PasswordStrength
        label={t('resetPassword.passwordStrength.input.label')}
        ariaLabel={t('resetPassword.passwordStrength.input.label')}
        value={password}
        placeholder={t('resetPassword.passwordStrength.input.placeholder')}
        onChange={handlePasswordChange}
        hint={t('resetPassword.passwordStrength.input.hint')}
        passwordStrengthWidth={width}
      />

      <SliderContainer>{sliderContent}</SliderContainer>
    </div>
  );
};

export default NewPasswordStrengthStory;
